import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import {
  DecodeHintType,
  BarcodeFormat,
  Result,
  Exception,
} from "@zxing/library";

type OnResult = (res: Result) => void;

const SUPPORTED_FORMATS: BarcodeFormat[] = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.PDF_417,
];


export interface UseZxingScannerReturn {
  isReady: boolean;
  stop: () => void;
}

export function useZxingScanner(
  videoElement: HTMLVideoElement | null,
  onResult: OnResult,
  active: boolean
): UseZxingScannerReturn {
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastScanRef = useRef<{ text: string; timestamp: number } | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const startedRef = useRef(false);
  const stopControls = useCallback((controls: IScannerControls | null | undefined) => {
    if (!controls) return;
    try {
      const stopFn = controls.stop?.bind(controls) as (() => unknown) | undefined;
      if (!stopFn) return;

      const result = stopFn();
      if (
        typeof result === "object" &&
        result !== null &&
        typeof (result as { catch?: unknown }).catch === "function"
      ) {
        (result as Promise<unknown>).catch((error) =>
          console.warn("[useZxingScanner] controls.stop async failed", error)
        );
      }
    } catch (error) {
      console.warn("[useZxingScanner] controls.stop failed", error);
    }
  }, []);
  const [isReady, setIsReady] = useState(false);

  const stop = useCallback(() => {
    stopControls(controlsRef.current);
    controlsRef.current = null;
    readerRef.current = null;

    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (videoRef.current) {
      (videoRef.current as any).srcObject = null;
    }
    lastScanRef.current = null;
    setIsReady(false);
    startedRef.current = false;
  }, [stopControls]);

  useEffect(() => {
    videoRef.current = videoElement;

    if (!active) {
      startedRef.current = false;
      stop();
      return;
    }

    if (!videoElement) {
      return;
    }

    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, SUPPORTED_FORMATS);

    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;

    let cancelled = false;

    (async () => {
      try {
        const devices =
          await BrowserMultiFormatReader.listVideoInputDevices();
        let deviceId: string | undefined;
        if (devices.length > 0) {
          const backFacing = devices.find((device: MediaDeviceInfo) =>
            /back|rear|environment/i.test(device.label)
          );
          const chosenDevice = backFacing ?? devices[0];
          deviceId = chosenDevice?.deviceId;
        }

        if (cancelled) {
          return;
        }

        const controls = await reader.decodeFromVideoDevice(
          deviceId,
          videoElement,
          (result: Result | undefined, callbackError: Exception | undefined) => {
            const now = Date.now();
            const last = lastScanRef.current?.timestamp ?? null;
            const timeSinceLast = typeof last === "number" ? now - last : null;

            if (callbackError) {
              // ignore callback errors; ZXing will retry
            }

            if (!result) {
              return;
            }

            const text = result.getText();
            if (!text) {
              return;
            }

            if (
              lastScanRef.current &&
              lastScanRef.current.text === text &&
              timeSinceLast !== null &&
              timeSinceLast < 1500
            ) {
              return;
            }

            lastScanRef.current = { text, timestamp: now };
            onResult(result);
          }
        );
        if (cancelled) {
          stopControls(controls);
          return;
        }
        controlsRef.current = controls;
        setIsReady(true);
      } catch (error) {
        console.error("ZXing scanner initialization failed:", error);
        setIsReady(false);
        startedRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      startedRef.current = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, videoElement, onResult, stop, stopControls]);

  return { isReady, stop };
}
























